

import React, { useState, useEffect, useRef } from "react";
import { X, Play } from "lucide-react";
import { COACH_CONFIG } from "../../coachconfig";

const API_BASE = process.env.REACT_APP_API_URL || "https://kinos-api.onrender.com";

const AREAS = [
  "Marketing & Advertising",
  "Business Consulting",
  "Coaching & Training",
  "Creative Services",
  "Technology & Software",
  "Health & Wellness",
  "Finance & Accounting",
  "Real Estate",
  "Legal Services",
  "Other"
];

const PLATFORMS = [
  "Instagram",
  "X (Twitter)",
  "TikTok",
  "LinkedIn",
  "YouTube",
  "Facebook"
];

export default function KinosLeadModal({ open, onClose, email }) {
  const [area, setArea] = useState("");
  const [socialHandle, setSocialHandle] = useState("");
  const [platform, setPlatform] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [modalEmail, setModalEmail] = useState(email || "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [platformDropdownOpen, setPlatformDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [firstName, setFirstName] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setModalEmail(email || "");
  }, [email]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const leadPayload = {
      firstName: firstName.trim(),
      email: modalEmail.trim(),
      area: area.trim(),
      platform: platform.trim(),
      socialHandle: socialHandle.trim(),
      userId: COACH_CONFIG.coachUserId,
      workspaceId: COACH_CONFIG.workspaceId,
    };

    try {
      await fetch(`${API_BASE}/api/kolanut-landing/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadPayload),
      });
    } catch (err) {
      console.warn('Lead notification request failed:', err);
    }

    try {
      const response = await fetch(`${API_BASE}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: modalEmail,
          firstName: firstName,
          social: `${platform}: ${socialHandle}`,
          area: area,
          source: 'Kolanut Landing Page',
          tags: ['Landing Page Interest', 'Demo Request']
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        setShowVideo(true);
        setMessage('Thanks for subscribing! Watch the demo below:');
      } else {
        setSubmitted(true);
        setShowVideo(true);
        setMessage('Watch the demo below:');
        console.warn('Mailchimp subscription failed:', result.error);
      }
    } catch (error) {
      console.error('Error adding to Mailchimp:', error);
      setSubmitted(true);
      setShowVideo(true);
      setMessage('Watch the demo below:');
    }

    setLoading(false);
  };

  const handleAreaSelect = (selectedArea) => {
    setArea(selectedArea);
    setDropdownOpen(false);
  };

  const handlePlatformSelect = (selectedPlatform) => {
    setPlatform(selectedPlatform);
    setPlatformDropdownOpen(false);
  };

  const handleFormSubmit = () => {
    // Validate required fields
    if (!modalEmail || !area || !socialHandle || !platform || !firstName) {
      alert("Please fill in all required fields");
      return;
    }
    
    // Call the original handleSubmit function
    const fakeEvent = { preventDefault: () => {} };
    handleSubmit(fakeEvent);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 md:flex md:items-center md:justify-center">
      <div className="bg-white w-full h-full md:rounded-2xl md:shadow-2xl md:max-w-5xl md:min-h-[700px] md:w-full md:h-auto p-0 relative md:border md:border-[#c4ffa0] overflow-hidden flex flex-col md:flex-row">
        {/* Close button */}
        <button
          className="absolute top-6 right-6 text-black hover:text-black/70 text-4xl z-20 bg-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition duration-200 hover:rotate-90 border border-[#c4ffa0]/30"
          onClick={onClose}
        >
          &times;
        </button>

        {/* Left Side (hidden on mobile) */}
        <div className="hidden md:flex md:w-1/2 flex-col items-center justify-center bg-[#c4ffa0]/10 p-8 md:p-10">
          <img
            src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80"
            alt="KinOS Visual"
            className="rounded-xl w-32 h-32 md:w-40 md:h-40 object-cover mb-4 shadow"
          />
          <span className="text-black font-bold text-xl mb-1">Kolanut AI</span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-black mb-2 text-center">
            Watch the Demo
          </h2>
        </div>

        {/* Right Side */}
        <div className="w-full md:w-1/2 bg-white p-4 md:p-10 flex flex-col justify-start min-h-full">
          {/* Mobile-only header */}
          <div className="block md:hidden mb-6 text-center pt-12">
            <span className="text-black font-bold text-xl">Kolanut AI</span>
            <h2 className="text-2xl font-extrabold text-black mt-2">
              Watch the Demo
            </h2>
          </div>

          {submitted ? (
            <div className="flex-1 flex flex-col items-center justify-center min-h-[400px] space-y-6">
              <div className="text-center text-black font-semibold text-lg">
                {message}
              </div>
              
              {showVideo && (
                <div className="w-full max-w-2xl">
                  <div className="relative w-full bg-black rounded-xl" style={{ paddingBottom: '56.25%' }}>
                    <video
                      ref={videoRef}
                      className="absolute top-0 left-0 w-full h-full object-contain rounded-xl"
                      controls
                      autoPlay
                      playsInline
                    >
                      <source src="/demo_video.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  
                  <div className="mt-4 text-center space-y-4">
                    <button
                      onClick={() => window.open("https://calendly.com/kolanut", "_blank")}
                      className="px-6 py-3 bg-[#c4ffa0] text-black font-semibold rounded-xl hover:bg-[#c4ffa0]/90 transition-all duration-300 hover:scale-105"
                    >
                      Book a Demo Call
                    </button>
                    
                    {/* Social Media Follow Section */}
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-3">Follow us for updates and tips:</p>
                      <div className="flex justify-center items-center gap-6">
                        <a
                          href="https://x.com/kolanutai"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
                          aria-label="Follow us on X"
                        >
                          <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 24 24" fill="#0f0f0f" aria-hidden="true">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </a>
                        <a
                          href="https://instagram.com/kolanut.ai"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
                          aria-label="Follow us on Instagram"
                        >
                          <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 24 24" aria-hidden="true">
                            <defs>
                              <linearGradient id="instagramGradientKinos" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#f09433" />
                                <stop offset="25%" stopColor="#e6683c" />
                                <stop offset="50%" stopColor="#dc2743" />
                                <stop offset="75%" stopColor="#cc2366" />
                                <stop offset="100%" stopColor="#bc1888" />
                              </linearGradient>
                            </defs>
                            <path fill="url(#instagramGradientKinos)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.136 4.771 1.69 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.85-.07-3.26-.136-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-1.801 0-3.33-1.529-3.33-3.33 0-1.801 1.529-3.33 3.33-3.33 1.802 0 3.33 1.529 3.33 3.33 0 1.801-1.528 3.33-3.33 3.33zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4 md:gap-8 px-1 md:px-0 pb-8 md:pb-12 flex-1">
              <div>
                <label className="block text-black font-semibold mb-2 text-base md:text-lg">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 bg-[#c4ffa0]/10 border border-[#c4ffa0] md:border-2 rounded-lg md:rounded-xl text-base md:text-lg text-black placeholder:text-black/60 focus:outline-none focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#c4ffa0] transition"
                />
              </div>

              <div>
                <label className="block text-black font-semibold mb-2 text-base md:text-lg">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={modalEmail}
                  onChange={(e) => setModalEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 bg-[#c4ffa0]/10 border border-[#c4ffa0] md:border-2 rounded-lg md:rounded-xl text-base md:text-lg text-black placeholder:text-black/60 focus:outline-none focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#c4ffa0] transition"
                />
              </div>

              <div className="relative">
                <label className="block text-black font-semibold mb-2 text-base md:text-lg">
                  Area of Expertise <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full px-4 py-3 md:px-5 md:py-4 bg-[#c4ffa0]/10 border border-[#c4ffa0] md:border-2 rounded-lg md:rounded-xl text-base md:text-lg text-black focus:outline-none focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#c4ffa0] transition text-left flex justify-between items-center"
                >
                  <span className={area ? "text-black" : "text-black/60"}>
                    {area || "Select an area"}
                  </span>
                  <svg
                    className={`w-5 h-5 md:w-6 md:h-6 text-black transition-transform duration-200 ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {dropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 md:mt-2 bg-white border border-[#c4ffa0] md:border-2 rounded-lg md:rounded-xl shadow-lg md:shadow-xl max-h-60 overflow-auto">
                    {AREAS.map((areaOption) => (
                      <button
                        key={areaOption}
                        type="button"
                        onClick={() => handleAreaSelect(areaOption)}
                        className="w-full px-4 py-3 md:px-5 md:py-4 text-left text-base md:text-lg text-black hover:bg-[#c4ffa0]/10 focus:bg-[#c4ffa0]/10 focus:outline-none transition-colors border-b border-[#c4ffa0]/20 last:border-b-0"
                      >
                        {areaOption}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-black font-semibold mb-2 text-base md:text-lg">
                    Platform <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setPlatformDropdownOpen(!platformDropdownOpen)}
                    className="w-full px-4 py-3 md:px-5 md:py-4 bg-[#c4ffa0]/10 border border-[#c4ffa0] md:border-2 rounded-lg md:rounded-xl text-base md:text-lg text-black focus:outline-none focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#c4ffa0] transition text-left flex justify-between items-center"
                  >
                    <span className={platform ? "text-black" : "text-black/60"}>
                      {platform || "Select platform"}
                    </span>
                    <svg
                      className={`w-5 h-5 md:w-6 md:h-6 text-black transition-transform duration-200 ${
                        platformDropdownOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {platformDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 md:mt-2 bg-white border border-[#c4ffa0] md:border-2 rounded-lg md:rounded-xl shadow-lg md:shadow-xl max-h-60 overflow-auto">
                      {PLATFORMS.map((platformOption) => (
                        <button
                          key={platformOption}
                          type="button"
                          onClick={() => handlePlatformSelect(platformOption)}
                          className="w-full px-4 py-3 md:px-5 md:py-4 text-left text-base md:text-lg text-black hover:bg-[#c4ffa0]/10 focus:bg-[#c4ffa0]/10 focus:outline-none transition-colors border-b border-[#c4ffa0]/20 last:border-b-0"
                        >
                          {platformOption}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-black font-semibold mb-2 text-base md:text-lg">
                    Handle <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="@yourhandle"
                    className="w-full px-4 py-3 md:px-5 md:py-4 bg-[#c4ffa0]/10 border border-[#c4ffa0] md:border-2 rounded-lg md:rounded-xl text-base md:text-lg text-black placeholder:text-black/60 focus:outline-none focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#c4ffa0] transition"
                    value={socialHandle}
                    onChange={(e) => setSocialHandle(e.target.value)}
                    required
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleFormSubmit}
                disabled={loading}
                className="w-full px-5 py-3 md:px-6 md:py-4 bg-[#c4ffa0] text-black text-sm md:text-lg font-semibold md:font-bold rounded-lg md:rounded-xl hover:shadow-lg md:hover:shadow-xl transition duration-300 hover:bg-[#b3ee90] flex justify-center items-center mt-2 md:mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Getting Started...' : 'Get Started'}
              </button>

              <div className="text-xs md:text-sm text-gray-500 mt-2 md:mt-4 text-center leading-relaxed">
                We respect your privacy. Your info will only be used to follow up about Kolanut AI.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}